import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import {
  SecondaryIconButton,
  SecondaryButton,
  ButtonIcon,
  BrandBadge,
  InfoBadge,
  NeutralInverseBadge,
  Spinner,
} from '@hz-design-system/web-ui';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';

import useCarousel from './hooks/useCarousel';
import NoImage from './NoImage';
import Thumbnails from './Thumbnails';
import HeroImage from './HeroImage';
import MediaGroupPills, { type TMediaGroupPillKey } from './MediaGroupPills';
import FavouriteButton from '../Listing/FavouriteButton';
import { trackGAEvent } from '../../../../utils/gaTracking';
import { GA_TRACK_EVENTS_ACTIONS } from '../../../../utils/constants';

import type { TImageMediaGroup, TListingGallery, TMediaGroup } from 'types/TListing';

import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import Classes from './Gallery.module.scss';

const CarouselDialog = lazy(() => import(/* webpackChunkName: "CarouselDialog" */ './CarouselDialog'));
const MediaOverviewDialog = lazy(() => import(/* webpackChunkName: "MediaOverviewDialog" */ './MediaOverviewDialog'));
const VideoModal = lazy(() => import(/* webpackChunkName: "VideoModal" */ './VideoModal'));

const EMPTY_MEDIA_GROUPS: TMediaGroup[] = [];
const EMPTY_MEDIA_GROUP_DATA = {
  imageMediaGroups: [] as TImageMediaGroup[],
  groupedImageUrls: [] as string[],
  groupKeys: [] as TMediaGroupPillKey[],
};

type TGalleryProps = {
  gallery: TListingGallery;
  title: string;
  itemId: string;
  showMicroTipText: boolean;
  showUrgencyText: boolean;
  withFavoriteButton?: boolean;
  favoritedCount: number;
  isSaved: boolean;
  isCarAd: boolean;
};

const Gallery = ({
  gallery: {
    imageUrls = [],
    mediaGroups = EMPTY_MEDIA_GROUPS,
    microTipText,
    media: { video, imageSizes, generatedVideoUrl },
    alt,
  },
  title,
  itemId,
  showMicroTipText,
  showUrgencyText,
  withFavoriteButton,
  favoritedCount,
  isSaved,
  isCarAd,
}: TGalleryProps) => {
  const { t } = useI18nContext();
  const galleryContainerElement = useRef<HTMLHeadingElement>(null);
  const hasImage = Boolean(imageUrls.length);
  const hasMediaGroups = mediaGroups.length > 0;
  const eligibleGeneratedVideoUrl = !video && !hasMediaGroups ? generatedVideoUrl?.trim() || undefined : undefined;
  const itemCount = imageUrls.length + (eligibleGeneratedVideoUrl ? 1 : 0);
  const hasGalleryItem = itemCount > 0;
  const [showCarouselDialog, setShowCarouselDialog] = useState(false);
  const [showMediaOverview, setShowMediaOverview] = useState(false);
  const [activeVideo, setActiveVideo] = useState<string>();
  const [imageIndex, setImageIndex] = useState(1);
  const [groupedImageIndex, setGroupedImageIndex] = useState(0);
  const [overviewActiveKey, setOverviewActiveKey] = useState<TMediaGroupPillKey | undefined>();
  const [itemIndex, setItemIndex] = useState(0);

  useEffect(() => {
    setItemIndex((current) => Math.min(current, Math.max(0, itemCount - 1)));
  }, [itemCount]);

  const { imageMediaGroups, groupedImageUrls, groupKeys } = useMemo(() => {
    if (!hasMediaGroups) {
      return EMPTY_MEDIA_GROUP_DATA;
    }

    const filteredImageMediaGroups = mediaGroups.filter(
      (mediaGroup): mediaGroup is TImageMediaGroup => mediaGroup.group !== 'video',
    );

    return {
      imageMediaGroups: filteredImageMediaGroups,
      groupedImageUrls: filteredImageMediaGroups.flatMap(({ imageUrls: groupImageUrls }) => groupImageUrls),
      groupKeys: mediaGroups.map(({ group }) => group),
    };
  }, [hasMediaGroups, mediaGroups]);

  const openCarouselDialog = (index?: number) => {
    trackGAEvent(GA_TRACK_EVENTS_ACTIONS.OPEN_PHOTO_GALLERY);
    if (typeof index === 'number') {
      setItemIndex(index);
    }
    setShowCarouselDialog(true);
  };

  const openMediaOverview = (key?: TMediaGroupPillKey) => {
    let initialActiveKey = key;

    if (initialActiveKey === undefined) {
      const activeImageUrl = imageUrls[imageIndex - 1]?.replace(/\.jpg$/i, '');
      initialActiveKey = activeImageUrl
        ? imageMediaGroups.find(({ imageUrls: groupImageUrls }) =>
            groupImageUrls.some((imageUrl) => imageUrl.replace(/\.jpg$/i, '') === activeImageUrl),
          )?.group
        : undefined;
    }

    trackGAEvent(GA_TRACK_EVENTS_ACTIONS.OPEN_PHOTO_GALLERY);
    setOverviewActiveKey(initialActiveKey ?? groupKeys[0]);
    setShowMediaOverview(true);
  };

  const handleGalleryImageClick = () => openMediaOverview();

  const closeMediaGallery = () => {
    trackGAEvent(GA_TRACK_EVENTS_ACTIONS.CLOSE_PHOTO_GALLERY);
    setShowCarouselDialog(false);
    setShowMediaOverview(false);
  };

  const openGroupedImage = (imageUrl: string) => {
    const selectedIndex = groupedImageUrls.indexOf(imageUrl);
    setGroupedImageIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setShowCarouselDialog(true);
  };

  const backToMediaOverview = () => {
    setShowCarouselDialog(false);
    setActiveVideo(undefined);
    setShowMediaOverview(true);
  };

  const openVideoModal = (videoUrl: string) => {
    trackGAEvent(GA_TRACK_EVENTS_ACTIONS.OPEN_VIDEO_GALLERY);
    setActiveVideo(videoUrl);
  };

  const closeVideoModal = () => {
    trackGAEvent(GA_TRACK_EVENTS_ACTIONS.CLOSE_VIDEO_GALLERY);
    setActiveVideo(undefined);
    setShowMediaOverview(false);
  };

  const changeCarouselIndex = (index: number) => {
    if (hasMediaGroups) setImageIndex(index + 1);
    else setItemIndex(index);
  };

  const { CarouselComponent, showCarousel } = useCarousel(itemCount, Boolean(eligibleGeneratedVideoUrl));

  const modalImageUrls = hasMediaGroups ? groupedImageUrls : imageUrls;
  const modalImageIndex = hasMediaGroups ? groupedImageIndex : itemIndex;
  const changeModalCarouselIndex = hasMediaGroups ? setGroupedImageIndex : setItemIndex;
  const modalItemCount = modalImageUrls.length + (eligibleGeneratedVideoUrl ? 1 : 0);

  return (
    <>
      <div ref={galleryContainerElement} className={Classes.root}>
        <div className={Classes.container}>
          {showMicroTipText && microTipText && <BrandBadge className={Classes.badge}>{microTipText}</BrandBadge>}
          {showUrgencyText && microTipText && <InfoBadge className={Classes.badge}>{microTipText}</InfoBadge>}
          {!hasGalleryItem && <NoImage />}
          {eligibleGeneratedVideoUrl && !CarouselComponent && (
            <div className={Classes.loadingVideo} role="status" aria-label={t('gallery.generatedVideoLoading')}>
              <Spinner absolute />
            </div>
          )}
          {hasImage && !eligibleGeneratedVideoUrl && !showCarousel && (
            <HeroImage imageUrl={imageUrls[0]} imageSizes={imageSizes} alt={alt} onClickHandler={openCarouselDialog} />
          )}
          {showCarousel && CarouselComponent && (
            <>
              <CarouselComponent
                imageUrls={imageUrls}
                imageSizes={imageSizes}
                videoUrl={eligibleGeneratedVideoUrl}
                videoTitle={t('gallery.videoLabel')}
                onClickItemHandler={hasMediaGroups ? handleGalleryImageClick : openCarouselDialog}
                onChangeHandler={changeCarouselIndex}
                index={hasMediaGroups ? imageIndex - 1 : itemIndex}
                isHidden={showCarouselDialog}
                alt={alt}
              />
              <NeutralInverseBadge className={Classes.carouselIndexContainer}>
                {(hasMediaGroups ? imageIndex - 1 : itemIndex) + 1}/{itemCount}
              </NeutralInverseBadge>
            </>
          )}
          <div className={Classes.actions}>
            {video && (
              <SecondaryButton onClick={() => openVideoModal(video)}>
                <ButtonIcon name="player-play" isStartIcon={false} />
                {t('gallery.videoLabel')}
              </SecondaryButton>
            )}
            {hasGalleryItem && (
              <SecondaryIconButton
                className={`${Classes.zoom} ${eligibleGeneratedVideoUrl && !hasImage ? Classes.zoomVideoOnly : ''}`}
                name="zoom-in"
                onClick={hasMediaGroups ? handleGalleryImageClick : () => openCarouselDialog()}
                ariaLabel={t('gallery.zoomLabel')}
              />
            )}
            {withFavoriteButton && (
              <FavouriteButton isSaved={isSaved} itemId={itemId} favoritedCount={favoritedCount} />
            )}
          </div>
        </div>

        {itemCount > 1 && (
          <Thumbnails
            onChange={changeCarouselIndex}
            imageUrls={imageUrls}
            imageSizes={imageSizes}
            activeImageIndex={hasMediaGroups ? imageIndex - 1 : itemIndex}
            imageFit={isCarAd ? 'contain' : 'cover'}
            videoUrl={eligibleGeneratedVideoUrl}
          />
        )}
        {hasMediaGroups && <MediaGroupPills groupKeys={groupKeys} onClick={openMediaOverview} />}
      </div>
      {hasMediaGroups && showMediaOverview && (
        <Suspense>
          <MediaOverviewDialog
            title={title}
            alt={alt}
            mediaGroups={mediaGroups}
            imageSizes={imageSizes}
            initialActiveKey={overviewActiveKey}
            onClose={closeMediaGallery}
            onImageClick={openGroupedImage}
            onVideoClick={openVideoModal}
          />
        </Suspense>
      )}
      {showCarouselDialog && CarouselComponent && modalItemCount > 0 && (
        <Suspense>
          <CarouselDialog
            onClose={closeMediaGallery}
            title={title}
            onBack={hasMediaGroups ? backToMediaOverview : undefined}
          >
            <div className={Classes.modalCarousel}>
              <CarouselComponent
                isModal
                index={modalImageIndex}
                imageUrls={modalImageUrls}
                imageSizes={imageSizes}
                videoUrl={eligibleGeneratedVideoUrl}
                videoTitle={t('gallery.videoLabel')}
                onChangeHandler={changeModalCarouselIndex}
                alt={alt}
              />
              <NeutralInverseBadge className={Classes.carouselIndexContainerForModal}>
                {modalItemCount > 1 ? (
                  <span>
                    {modalImageIndex + 1}/{modalItemCount}
                  </span>
                ) : eligibleGeneratedVideoUrl ? (
                  <span>1/1</span>
                ) : (
                  <Trans i18nKey="gallery.image" count={modalImageIndex + 1} tagName="span" />
                )}
              </NeutralInverseBadge>
            </div>
            {modalItemCount > 1 && (
              <div className={Classes.dialogThumbnails}>
                <Thumbnails
                  onChange={changeModalCarouselIndex}
                  imageUrls={modalImageUrls}
                  imageSizes={imageSizes}
                  activeImageIndex={modalImageIndex}
                  imageFit={isCarAd ? 'contain' : 'cover'}
                  videoUrl={eligibleGeneratedVideoUrl}
                  loadAll
                />
              </div>
            )}
          </CarouselDialog>
        </Suspense>
      )}
      {activeVideo && (
        <Suspense>
          <VideoModal
            video={activeVideo}
            toggleVideoModal={closeVideoModal}
            onBack={hasMediaGroups ? backToMediaOverview : undefined}
            title={t('gallery.video-title')}
          />
        </Suspense>
      )}
    </>
  );
};

export default Gallery;
